<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';
import {
  PaymentReconciliationService,
  type TReconciliationProcessor,
  type TReconciliationFindingKind,
  type TReconciliationOutcome,
  type IReconciliationFinding,
} from '@/services/payment-reconciliation.services';

const props = defineProps<{ storeId?: string }>();

const { t } = useI18n({ useScope: 'global' });
const queryClient = useQueryClient();

// ── Sélecteur de commerce (uniquement quand aucun storeId n'est imposé par le
// parent, ex. la page globale — dans l'onglet Paiement d'un commerce, storeId
// est déjà fixé par CommercePaymentsTab et ce sélecteur n'a pas lieu d'être) ─
const showStorePicker = computed(() => !props.storeId);
const pendingStoreId = ref('');
const appliedStoreId = ref('');
const effectiveStoreId = computed(() =>
  props.storeId ? props.storeId : appliedStoreId.value || undefined,
);

const { data: restaurantsData } = useQuery({
  queryKey: ['reconciliation-restaurants'],
  queryFn: async () => {
    const res = await axiosInstance.get(
      'restaurants/search?pagination=false&sortBy=name&order=ASC',
    );
    return res.data;
  },
  enabled: showStorePicker,
  staleTime: 5 * 60_000,
});
const restaurants = computed<any[]>(() => {
  const d = restaurantsData.value as any;
  const list = d?.data ?? d?.restaurants ?? [];
  return Array.isArray(list) ? list : [];
});
function restaurantName(id: string | null | undefined) {
  if (!id) return '';
  const r = restaurants.value.find((r: any) => r.id === id);
  return r ? (r.designation ?? r.name ?? id) : id;
}

const storeDropdownOpen = ref(false);
const storeSearch = ref('');
const filteredStores = computed(() => {
  const q = storeSearch.value.trim().toLowerCase();
  if (!q) return restaurants.value;
  return restaurants.value.filter((r: any) =>
    (r.designation ?? r.name ?? '').toLowerCase().includes(q),
  );
});
function selectStore(id: string) {
  pendingStoreId.value = id;
  storeDropdownOpen.value = false;
}
function clearStore() {
  pendingStoreId.value = '';
  storeDropdownOpen.value = false;
}
const storeFilterDirty = computed(
  () => pendingStoreId.value !== appliedStoreId.value,
);
function applyStoreFilter() {
  appliedStoreId.value = pendingStoreId.value;
  storeDropdownOpen.value = false;
}

const PROCESSOR_OPTIONS: TReconciliationProcessor[] = [
  'mock',
  'market_pay',
  'on_site',
  'payfast',
];
const KIND_OPTIONS: TReconciliationFindingKind[] = [
  'MISSING_SETTLEMENT',
  'UNRECORDED_REFUND',
  'STALE_SETTLEMENT',
  'ORPHAN_TRANSACTION',
];
const OUTCOME_OPTIONS: TReconciliationOutcome[] = ['REPAIRED', 'BLOCKED'];

// ── Filtres ──────────────────────────────────────────────────────────────────
const processorFilter = ref<TReconciliationProcessor[]>([]);
const kindFilter = ref<TReconciliationFindingKind[]>([]);
const outcomeFilter = ref<TReconciliationOutcome[]>([]);
const referenceFilter = ref('');
const unresolvedOnly = ref(true);
const sortBy = ref<'createdAt' | 'updatedAt' | 'amount'>('createdAt');
const sortOrder = ref<'ASC' | 'DESC'>('DESC');

function toggleArrayFilter<T>(list: T[], value: T) {
  const idx = list.indexOf(value);
  if (idx >= 0) list.splice(idx, 1);
  else list.push(value);
}
function toggleSortOrder() {
  sortOrder.value = sortOrder.value === 'DESC' ? 'ASC' : 'DESC';
}

// ── Pagination ────────────────────────────────────────────────────────────────
const page = ref(1);
const PAGE_SIZES = [10, 20, 50] as const;
const pageSize = ref<(typeof PAGE_SIZES)[number]>(20);

watch(
  [
    effectiveStoreId,
    processorFilter,
    kindFilter,
    outcomeFilter,
    referenceFilter,
    unresolvedOnly,
    sortBy,
    sortOrder,
    pageSize,
  ],
  () => {
    page.value = 1;
  },
  { deep: true },
);

const findingsQueryKey = computed(() => [
  'reconciliation-findings',
  effectiveStoreId.value ?? 'all',
  page.value,
  pageSize.value,
  processorFilter.value,
  kindFilter.value,
  outcomeFilter.value,
  referenceFilter.value,
  unresolvedOnly.value,
  sortBy.value,
  sortOrder.value,
]);

const {
  data: findingsData,
  isPending: findingsLoading,
  isError: findingsError,
} = useQuery({
  queryKey: findingsQueryKey,
  queryFn: async () => {
    const res = await PaymentReconciliationService.searchFindings({
      storeId: effectiveStoreId.value,
      page: page.value,
      pageSize: pageSize.value,
      sortBy: sortBy.value,
      sortOrder: sortOrder.value,
      processor: processorFilter.value.length
        ? processorFilter.value
        : undefined,
      kind: kindFilter.value.length ? kindFilter.value : undefined,
      outcome: outcomeFilter.value.length ? outcomeFilter.value : undefined,
      reference: referenceFilter.value || undefined,
      unresolvedOnly: unresolvedOnly.value || undefined,
    });
    return res.data;
  },
});

const findings = computed<IReconciliationFinding[]>(
  () => findingsData.value?.data ?? [],
);
const findingsTotal = computed(() => findingsData.value?.total ?? 0);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(findingsTotal.value / pageSize.value)),
);

function invalidateFindings() {
  return queryClient.invalidateQueries({
    queryKey: ['reconciliation-findings'],
  });
}

// ── Sweep ────────────────────────────────────────────────────────────────────
const sweepRunning = ref(false);
const sweepError = ref('');
const sweepResult = ref<
  | Awaited<
      ReturnType<typeof PaymentReconciliationService.runSweep>
    >['data']['data']
  | null
>(null);

async function runSweep() {
  sweepRunning.value = true;
  sweepError.value = '';
  try {
    const res = await PaymentReconciliationService.runSweep(
      effectiveStoreId.value,
    );
    sweepResult.value = res.data.data;
    await invalidateFindings();
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    sweepError.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || t('paymentReconciliation.sweepError');
  } finally {
    sweepRunning.value = false;
  }
}

// ── Actions par finding ──────────────────────────────────────────────────────
const actioningId = ref<string | null>(null);
const actionError = ref('');

async function applyFinding(f: IReconciliationFinding) {
  actioningId.value = f.id;
  actionError.value = '';
  try {
    await PaymentReconciliationService.applyFinding(f.id);
    await invalidateFindings();
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    actionError.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || t('paymentReconciliation.applyError');
  } finally {
    actioningId.value = null;
  }
}

async function resolveFinding(f: IReconciliationFinding) {
  actioningId.value = f.id;
  actionError.value = '';
  try {
    await PaymentReconciliationService.resolveFinding(f.id);
    await invalidateFindings();
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    actionError.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || t('paymentReconciliation.resolveError');
  } finally {
    actioningId.value = null;
  }
}

// ── Helpers d'affichage ──────────────────────────────────────────────────────
function formatAmount(f: IReconciliationFinding) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: f.currency,
    }).format(f.amount);
  } catch {
    return `${f.amount} ${f.currency}`;
  }
}
function formatDate(d: string | null | undefined) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
function kindLabel(k: string) {
  return t(`paymentReconciliation.kind.${k.toLowerCase()}`);
}
function outcomeLabel(o: string) {
  return t(`paymentReconciliation.outcome.${o.toLowerCase()}`);
}
function blockedReasonLabel(r: string | null) {
  if (!r) return '—';
  return t(`paymentReconciliation.blockedReason.${r.toLowerCase()}`);
}
function processorLabel(p: string) {
  return t(`paymentsMgmt.processor.${p}`);
}
function outcomeColor(o: string) {
  return o === 'REPAIRED' ? '#10b981' : '#ef4444';
}
</script>

<template>
  <div class="prp" @click="storeDropdownOpen = false">
    <div v-if="showStorePicker" class="prp__store-bar" @click.stop>
      <div class="prp__dropdown-wrap">
        <button
          class="prp__dropdown-btn"
          :class="{ 'prp__dropdown-btn--active': pendingStoreId }"
          @click="storeDropdownOpen = !storeDropdownOpen"
        >
          <i class="pi pi-shop" />
          <span>{{
            restaurantName(pendingStoreId) ||
            t('paymentReconciliation.allStores')
          }}</span>
          <i
            v-if="pendingStoreId"
            class="pi pi-times"
            @click.stop="clearStore"
          />
          <i v-else class="pi pi-chevron-down" />
        </button>
        <Transition name="prp-drop">
          <div v-if="storeDropdownOpen" class="prp__dropdown-panel" @click.stop>
            <div class="prp__dropdown-search-wrap">
              <i class="pi pi-search" />
              <input
                v-model="storeSearch"
                class="prp__dropdown-search"
                :placeholder="t('paymentReconciliation.searchStorePlaceholder')"
              />
            </div>
            <div class="prp__dropdown-list">
              <button
                class="prp__dropdown-item"
                :class="{ 'prp__dropdown-item--active': !pendingStoreId }"
                @click="clearStore"
              >
                {{ t('paymentReconciliation.allStores') }}
              </button>
              <button
                v-for="r in filteredStores"
                :key="r.id"
                class="prp__dropdown-item"
                :class="{
                  'prp__dropdown-item--active': pendingStoreId === r.id,
                }"
                @click="selectStore(r.id)"
              >
                {{ r.designation ?? r.name }}
              </button>
              <p v-if="!filteredStores.length" class="prp__dropdown-empty">
                {{ t('paymentsMgmt.noResults') }}
              </p>
            </div>
          </div>
        </Transition>
      </div>

      <button
        class="prp__btn prp__btn--ghost"
        :disabled="!storeFilterDirty"
        @click="applyStoreFilter"
      >
        <i class="pi pi-filter" />
        {{ t('paymentReconciliation.applyStoreFilter') }}
      </button>

      <span v-if="appliedStoreId" class="prp__store-active">
        <i class="pi pi-shop" />
        {{ restaurantName(appliedStoreId) }}
      </span>
    </div>

    <div class="prp__sweep-bar">
      <button
        class="prp__btn prp__btn--primary"
        :disabled="sweepRunning"
        @click="runSweep"
      >
        <i :class="sweepRunning ? 'pi pi-spin pi-spinner' : 'pi pi-sync'" />
        {{ t('paymentReconciliation.runSweep') }}
      </button>

      <label class="prp__checkbox">
        <input
          v-model="unresolvedOnly"
          type="checkbox"
          class="prp__checkbox-input"
        />
        <span class="prp__checkbox-box">
          <i class="pi pi-check" />
        </span>
        {{ t('paymentReconciliation.unresolvedOnly') }}
      </label>

      <span class="prp__count"
        >{{ findingsTotal }} {{ t('paymentReconciliation.results') }}</span
      >
    </div>

    <Transition name="prp-fade">
      <div v-if="sweepResult" class="prp__sweep-result">
        <div class="prp__sweep-stat">
          <span class="prp__sweep-stat-val">{{ sweepResult.storesSwept }}</span>
          <span class="prp__sweep-stat-label">{{
            t('paymentReconciliation.storesSwept')
          }}</span>
        </div>
        <div class="prp__sweep-stat">
          <span class="prp__sweep-stat-val">{{
            sweepResult.transactionsSeen
          }}</span>
          <span class="prp__sweep-stat-label">{{
            t('paymentReconciliation.transactionsSeen')
          }}</span>
        </div>
        <div class="prp__sweep-stat">
          <span class="prp__sweep-stat-val prp__sweep-stat-val--ok">{{
            sweepResult.repaired
          }}</span>
          <span class="prp__sweep-stat-label">{{
            t('paymentReconciliation.repaired')
          }}</span>
        </div>
        <div class="prp__sweep-stat">
          <span class="prp__sweep-stat-val prp__sweep-stat-val--bad">{{
            sweepResult.blocked
          }}</span>
          <span class="prp__sweep-stat-label">{{
            t('paymentReconciliation.blocked')
          }}</span>
        </div>
        <div class="prp__sweep-stat">
          <span class="prp__sweep-stat-val">{{
            sweepResult.unresolvedFindings
          }}</span>
          <span class="prp__sweep-stat-label">{{
            t('paymentReconciliation.unresolvedFindings')
          }}</span>
        </div>
      </div>
    </Transition>

    <Transition name="prp-fade">
      <div v-if="sweepError" class="prp__banner prp__banner--error">
        <i class="pi pi-exclamation-circle" /> {{ sweepError }}
      </div>
    </Transition>

    <!-- Filtres -->
    <div class="prp__filters">
      <div class="prp__filters-row">
        <div class="prp__search-wrap">
          <i class="pi pi-search prp__search-icon" />
          <input
            v-model="referenceFilter"
            class="prp__search"
            :placeholder="t('paymentReconciliation.referencePlaceholder')"
          />
        </div>

        <select v-model="sortBy" class="prp__select">
          <option value="createdAt">
            {{ t('paymentsMgmt.sortCreated') }}
          </option>
          <option value="updatedAt">
            {{ t('paymentsMgmt.sortUpdated') }}
          </option>
          <option value="amount">{{ t('paymentsMgmt.sortAmount') }}</option>
        </select>
        <button
          class="prp__icon-btn"
          :title="t('paymentsMgmt.toggleOrder')"
          @click="toggleSortOrder"
        >
          <i
            :class="
              sortOrder === 'DESC'
                ? 'pi pi-sort-amount-down'
                : 'pi pi-sort-amount-up'
            "
          />
        </button>

        <select v-model.number="pageSize" class="prp__select">
          <option v-for="ps in PAGE_SIZES" :key="ps" :value="ps">
            {{ ps }} {{ t('paymentsMgmt.perPage') }}
          </option>
        </select>
      </div>

      <div class="prp__filters-row">
        <div class="prp__chip-group">
          <span class="prp__chip-group-label">{{
            t('paymentReconciliation.kind.label')
          }}</span>
          <button
            v-for="k in KIND_OPTIONS"
            :key="k"
            class="prp__chip"
            :class="{ 'prp__chip--active': kindFilter.includes(k) }"
            @click="toggleArrayFilter(kindFilter, k)"
          >
            {{ kindLabel(k) }}
          </button>
        </div>
      </div>

      <div class="prp__filters-row">
        <div class="prp__chip-group">
          <span class="prp__chip-group-label">{{
            t('paymentReconciliation.outcome.label')
          }}</span>
          <button
            v-for="o in OUTCOME_OPTIONS"
            :key="o"
            class="prp__chip"
            :class="{ 'prp__chip--active': outcomeFilter.includes(o) }"
            @click="toggleArrayFilter(outcomeFilter, o)"
          >
            {{ outcomeLabel(o) }}
          </button>
        </div>

        <div class="prp__chip-group">
          <span class="prp__chip-group-label">{{
            t('paymentsMgmt.processor.label')
          }}</span>
          <button
            v-for="p in PROCESSOR_OPTIONS"
            :key="p"
            class="prp__chip"
            :class="{ 'prp__chip--active': processorFilter.includes(p) }"
            @click="toggleArrayFilter(processorFilter, p)"
          >
            {{ processorLabel(p) }}
          </button>
        </div>
      </div>
    </div>

    <Transition name="prp-fade">
      <div v-if="actionError" class="prp__banner prp__banner--error">
        <i class="pi pi-exclamation-circle" /> {{ actionError }}
      </div>
    </Transition>

    <!-- Table findings -->
    <div v-if="findingsLoading" class="prp__empty">
      <i class="pi pi-spin pi-spinner" />
    </div>
    <div v-else-if="findingsError" class="prp__empty">
      <i class="pi pi-exclamation-circle" />
      <p>{{ t('paymentReconciliation.loadError') }}</p>
    </div>
    <div v-else-if="!findings.length" class="prp__empty">
      <i class="pi pi-check-circle" />
      <p>{{ t('paymentReconciliation.empty') }}</p>
    </div>

    <div v-else class="prp__table-wrap">
      <table class="prp__table">
        <thead>
          <tr>
            <th>{{ t('paymentReconciliation.colKind') }}</th>
            <th>{{ t('paymentReconciliation.colOutcome') }}</th>
            <th>{{ t('paymentReconciliation.colAmount') }}</th>
            <th>{{ t('paymentReconciliation.colProcessor') }}</th>
            <th>{{ t('paymentReconciliation.colReference') }}</th>
            <th>{{ t('paymentReconciliation.colBlockedReason') }}</th>
            <th>{{ t('paymentReconciliation.colDate') }}</th>
            <th class="prp__th-actions"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="f in findings" :key="f.id" class="prp__tr">
            <td>{{ kindLabel(f.kind) }}</td>
            <td>
              <span
                class="prp__pill"
                :style="{
                  background: outcomeColor(f.outcome) + '1a',
                  color: outcomeColor(f.outcome),
                  borderColor: outcomeColor(f.outcome) + '40',
                }"
              >
                {{ outcomeLabel(f.outcome) }}
              </span>
            </td>
            <td class="prp__td-amount">{{ formatAmount(f) }}</td>
            <td>{{ processorLabel(f.processor) }}</td>
            <td class="prp__td-muted">{{ f.reference ?? '—' }}</td>
            <td class="prp__td-muted">
              {{ blockedReasonLabel(f.blockedReason) }}
            </td>
            <td class="prp__td-muted">{{ formatDate(f.createdAt) }}</td>
            <td class="prp__td-actions">
              <div v-if="!f.resolvedAt" class="prp__row-actions">
                <button
                  v-if="f.outcome === 'BLOCKED'"
                  class="prp__btn prp__btn--ghost prp__btn--sm"
                  :disabled="actioningId === f.id"
                  @click="applyFinding(f)"
                >
                  <i
                    v-if="actioningId === f.id"
                    class="pi pi-spin pi-spinner"
                  />
                  <i v-else class="pi pi-wrench" />
                  {{ t('paymentReconciliation.apply') }}
                </button>
                <button
                  class="prp__btn prp__btn--ghost prp__btn--sm"
                  :disabled="actioningId === f.id"
                  @click="resolveFinding(f)"
                >
                  <i class="pi pi-check" />
                  {{ t('paymentReconciliation.resolve') }}
                </button>
              </div>
              <span v-else class="prp__resolved">
                <i class="pi pi-check-circle" />
                {{ t('paymentReconciliation.resolved') }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="prp__pagination">
      <button class="prp__pg-btn" :disabled="page === 1" @click="page = 1">
        <i class="pi pi-angle-double-left" />
      </button>
      <button class="prp__pg-btn" :disabled="page === 1" @click="page--">
        <i class="pi pi-chevron-left" />
      </button>
      <span class="prp__pg-info">{{ page }} / {{ totalPages }}</span>
      <button
        class="prp__pg-btn"
        :disabled="page === totalPages"
        @click="page++"
      >
        <i class="pi pi-chevron-right" />
      </button>
      <button
        class="prp__pg-btn"
        :disabled="page === totalPages"
        @click="page = totalPages"
      >
        <i class="pi pi-angle-double-right" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.prp {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.prp__store-bar {
  position: relative;
  z-index: 60;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
}
.prp__store-active {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--nxt-on-surface-variant);
}
.prp__dropdown-wrap {
  position: relative;
  min-width: 16rem;
}
.prp__dropdown-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  height: 2.5rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  box-sizing: border-box;
}
.prp__dropdown-btn span {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}
.prp__dropdown-btn--active {
  border-color: #ffd60a;
  color: #0b1320;
}
.prp__dropdown-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 200;
  min-width: 16rem;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}
.prp__dropdown-search-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.prp__dropdown-search-wrap i {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}
.prp__dropdown-search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
}
.prp__dropdown-list {
  max-height: 220px;
  overflow-y: auto;
  padding: 0.375rem;
}
.prp__dropdown-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: none;
  background: none;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  cursor: pointer;
}
.prp__dropdown-item:hover {
  background: var(--nxt-surface-mid);
}
.prp__dropdown-item--active {
  background: var(--nxt-surface-mid);
  font-weight: 700;
  color: #0b1320;
}
.prp__dropdown-empty {
  margin: 0;
  padding: 0.75rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  text-align: center;
}
.prp-drop-enter-active,
.prp-drop-leave-active {
  transition:
    opacity 0.15s,
    transform 0.15s;
}
.prp-drop-enter-from,
.prp-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.prp__sweep-bar {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}
.prp__checkbox {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  user-select: none;
}
.prp__checkbox-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
.prp__checkbox-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  flex-shrink: 0;
  border: 1.5px solid var(--nxt-outline-variant);
  border-radius: 0.3rem;
  background: var(--nxt-card-bg);
  transition:
    background 0.15s,
    border-color 0.15s;
}
.prp__checkbox-box i {
  font-size: 0.625rem;
  font-weight: 900;
  color: #0b1320;
  opacity: 0;
  transform: scale(0.6);
  transition:
    opacity 0.15s,
    transform 0.15s;
}
.prp__checkbox-input:checked + .prp__checkbox-box {
  background: #ffd60a;
  border-color: #ffd60a;
}
.prp__checkbox-input:checked + .prp__checkbox-box i {
  opacity: 1;
  transform: scale(1);
}
.prp__checkbox-input:focus-visible + .prp__checkbox-box {
  outline: 2px solid #ffd60a;
  outline-offset: 2px;
}
.prp__count {
  margin-left: auto;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  white-space: nowrap;
}

.prp__sweep-result {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1rem;
  padding: 1rem 1.25rem;
}
.prp__sweep-stat {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.prp__sweep-stat-val {
  font-size: 1.375rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.prp__sweep-stat-val--ok {
  color: #10b981;
}
.prp__sweep-stat-val--bad {
  color: #ef4444;
}
.prp__sweep-stat-label {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--nxt-on-surface-muted);
}

.prp__banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
}
.prp__banner--error {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.prp__filters {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  padding: 1rem;
}
.prp__filters-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
}
.prp__search-wrap {
  position: relative;
  flex: 1;
  min-width: 12rem;
}
.prp__search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.prp__search {
  width: 100%;
  height: 2.5rem;
  padding: 0 0.875rem 0 2.25rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
}
.prp__select {
  height: 2.5rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
}
.prp__icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.625rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
  cursor: pointer;
  flex-shrink: 0;
}

.prp__chip-group {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
}
.prp__chip-group-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
  margin-right: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.prp__chip {
  padding: 0.3rem 0.75rem;
  border-radius: 9999px;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.75rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
}
.prp__chip--active {
  border-color: #ffd60a;
  background: rgba(255, 214, 10, 0.12);
  color: #0b1320;
  font-weight: 700;
}

.prp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.5rem;
  padding: 0 1.125rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: none;
  white-space: nowrap;
}
.prp__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.prp__btn--primary {
  background: #ffd60a;
  color: #0b1320;
}
.prp__btn--ghost {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
.prp__btn--sm {
  height: 2rem;
  padding: 0 0.75rem;
  font-size: 0.75rem;
}

.prp__table-wrap {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 1.5rem;
  overflow: auto;
}
.prp__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
}
.prp__table th {
  text-align: left;
  padding: 0.875rem 1rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-bottom: 1px solid var(--nxt-outline-variant);
  white-space: nowrap;
}
.prp__tr td {
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
  color: var(--nxt-on-surface);
  white-space: nowrap;
}
.prp__td-amount {
  font-weight: 700;
}
.prp__td-muted {
  color: var(--nxt-on-surface-muted);
}
.prp__th-actions,
.prp__td-actions {
  width: 1%;
}
.prp__row-actions {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.prp__resolved {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  color: #10b981;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}

.prp__pill {
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 0.2rem 0.625rem;
  border-radius: 0.4rem;
  border: 1px solid;
  white-space: nowrap;
}

.prp__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3rem 1rem;
  color: var(--nxt-on-surface-muted);
}
.prp__empty i {
  font-size: 1.75rem;
  opacity: 0.4;
}

.prp__pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}
.prp__pg-btn {
  min-width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-family: inherit;
  cursor: pointer;
}
.prp__pg-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.prp__pg-info {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--nxt-on-surface-variant);
}

.prp-fade-enter-active,
.prp-fade-leave-active {
  transition: opacity 0.2s;
}
.prp-fade-enter-from,
.prp-fade-leave-to {
  opacity: 0;
}
</style>

<style>
[data-theme='dark'] .prp__sweep-result,
[data-theme='dark'] .prp__filters,
[data-theme='dark'] .prp__table-wrap {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .prp__search,
[data-theme='dark'] .prp__select,
[data-theme='dark'] .prp__icon-btn,
[data-theme='dark'] .prp__chip,
[data-theme='dark'] .prp__checkbox-box,
[data-theme='dark'] .prp__dropdown-btn,
[data-theme='dark'] .prp__pg-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .prp__dropdown-panel {
  background: #111c2e;
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .prp__dropdown-item:hover,
[data-theme='dark'] .prp__dropdown-item--active {
  background: rgba(255, 255, 255, 0.08);
  color: #ffd60a;
}
[data-theme='dark'] .prp__checkbox-input:checked + .prp__checkbox-box {
  background: #ffd60a;
  border-color: #ffd60a;
}
[data-theme='dark'] .prp__chip--active {
  background: rgba(255, 214, 10, 0.15);
  color: #ffd60a;
}
</style>
